// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CheckoutReviewAndSubmitPageContext } from "@insite/content-library/Pages/CheckoutReviewAndSubmitPage";
import { CheckoutShippingPageContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import Button from "@insite/mobius/Button";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import xExpressCheckout from "@rugby/App/Handlers/Checkout/ExpressCheckout";
import { xCheckoutStepperUpdate, xCheckoutStepsTotalUpdate } from "@rugby/App/Handlers/Checkout/UpdateCheckout";
import xUpdateExpressCheckout from "@rugby/App/Handlers/Checkout/UpdateExpressCheckout";
import { ExpressCheckoutCollectionModel } from "@rugby/App/Services/ExpressCheckoutService";
import { xAchState, xCheckoutActions, xCheckoutState, xExpressCheckoutState } from "@rugby/App/Store/Reducers";
import { getAchError, getIsEditing, getSelectedAch } from "@rugby/App/Store/Selectors/AchSelectors";
import { getExpressCheckout } from "@rugby/App/Store/Selectors/ExpressCheckoutSelectors";
import Link, { LinkPresentationProps } from "@rugby/MobiusOverrides/Link";
import { screen } from "@rugby/XDL/Helpers";
import { color } from "@rugby/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { connect, DispatchProp, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: xCheckoutState & xExpressCheckoutState & ApplicationState & xAchState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const customSettings: any = getSettingsCollection(state).customSettings;
    const achPaymentMethodName = customSettings["achPaymentMethodName"] ? customSettings["achPaymentMethodName"] : "";

    return {
        fulfillmentMethod: state.context.session.fulfillmentMethod,
        step: state.xCheckout.step,
        stepsTotal: state.xCheckout.stepsTotal,
        nextButtonDisabled: state.xCheckout.nextButtonDisabled,
        checkoutShippingPageLink: getPageLinkByPageType(state, "CheckoutShippingPage"),
        cartId: state.pages?.checkoutReviewAndSubmit,
        showPlaceOrderButton: canPlaceOrder(cartState.value),
        showSubmitForApprovalOrder: canSubmitForApprovalOrder(cartState.value),
        expressCheckoutState: getExpressCheckout(state),
        currentPage: getCurrentPage(state),
        paymentMethodName: state.xCheckout.cart.paymentMethodName,
        achPaymentMethodName,
        achError: getAchError(state),
        selectedAchAccountNumber: getSelectedAch(state)?.accountNumber ?? cartState.value?.properties.achAccountNumber,
        achIsEditing: getIsEditing(state),
        allowedPaymentMethodsForCheckout: customSettings.allowedPaymentMethodsForCheckout,
    };
};

const mapDispatchToProps = {
    xCheckoutStepperUpdate,
    xCheckoutStepsTotalUpdate,
    preloadCheckoutShippingData,
    xExpressCheckout,
    xUpdateExpressCheckout,
    updateCart: makeHandlerChainAwaitable(updateCart),
};

type Props = WidgetProps &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    DispatchProp<xCheckoutActions> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutShippingHeaderStyles {
    container?: InjectableCss;
    stepsContainer?: InjectableCss<any>;
    step?: InjectableCss;
    buttonWrapper?: InjectableCss;
    button?: InjectableCss;
    progressIndicator?: InjectableCss<any>;
}

export const styles: CheckoutShippingHeaderStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: space-between;
            height: 80px;
            padding: 20px 0;

            ${screen("lte", "md")`
                height: 160px;
            `}
        `,
    },
    stepsContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            position: relative;
            width: calc(100% - 300px);

            & .stepNode {
                ${(props: { stepsTotal: number }) => {
                    if (props.stepsTotal === 4) {
                        return `width: 25%`;
                    }

                    if (props.stepsTotal === 3) {
                        return `width: 33.3%;  padding-left: 50px;`;
                    }

                    if (props.stepsTotal === 2) {
                        return `width: 50%;  padding-left: 50px;`;
                    }
                }}
            }
            & .stepNode {
                pointer-events: none;

                &:focus {
                    outline: none;
                }

                &.active,
                &.done {
                    pointer-events: initial;
                }
            }

            ${screen("lte", "md")`
                &,  & .stepNode{
                    width: 100% ;
                    height: 40px;
                    margin-bottom: 15px;
                }
            `}
        `,
    },

    buttonWrapper: {
        css: css`
            position: relative;
            width: 270px;
            margin-left: auto;

            ${screen("lte", "md")`
                width: 100%;
            `}
        `,
    },
    button: {
        css: css`
            position: absolute;
            transition: all 500ms ease-out;
            width: 125px;

            &.hide {
                z-index: 1;
                visibility: hidden;
            }

            &.full-width {
                width: 270px;
                z-index: 2;

                ${screen("lte", "md")`
                    width: 100%;
                    height: 40px;
                `}
            }

            ${screen("lte", "md")`
                width: calc(50% - 10px);
            `}

            &.next {
                right: 0;
            }

            &.prev {
                margin-right: 20px;
                left: 0;
            }
        `,
    },
    step: {
        css: css`
            & > span {
                color: ${color("common.ui2")} !important;
                font-size: 18px;
                line-height: 40px;
                letter-spacing: 0;
                height: 40px;
                z-index: 2;
                padding: 0 20px;
                transition: all 500ms ease-out;
            }

            ${screen("lte", "md")`
                &:not(.active) {
                    display: none;
                }
            `}

            &.done > span {
                color: ${color("text.link")} !important;
            }

            &.active > span {
                color: ${color("text.main")} !important;
            }
        `,
    },
    progressIndicator: {
        css: css`
            position: absolute;
            transition: all 500ms ease-out;
            z-index: 1;
            background: ${color("common.white")};
            height: 40px;
            z-index: 1;
            box-shadow: 12px 2px 14px -2px rgba(80, 106, 113, 0.25);

            ${(props: { step: number; stepsTotal: number }) => {
                if (props.stepsTotal === 4) {
                    return `width: calc(${25 * props.step}%  - 30px);`;
                }

                if (props.stepsTotal === 3) {
                    return `width: calc(${33.3 * (props.step === 1 ? 1 : props.step)}%  - 30px);`;
                }

                if (props.stepsTotal === 2) {
                    return `width: calc(${50 * (props.step === 1 ? 1 : 2)}%  - 30px);`;
                }
            }}

            ${screen("lte", "sm")`
               width: calc(100%  - 30px);
            `}

            :before {
                content: "";
                position: absolute;
                right: -26px;
                top: 0px;
                width: 0;
                height: 0;
                border-left: 24px solid rgb(212 214 215 / 25%);
                border-top: 20px solid transparent;
                border-bottom: 20px solid transparent;
            }

            :after {
                content: "";
                position: absolute;
                right: -25px;
                top: 0px;
                width: 0;
                height: 0;
                border-left: 25px solid ${color("common.white")};
                border-top: 20px solid transparent;
                border-bottom: 20px solid transparent;
            }
        `,
    },
};

const stepsAsString: { [key: string]: number } = {
    Addresses: 1,
    Shipping: 2,
    Payment: 3,
    Review: 4,
};

const CheckoutStepper: FC<Props> = ({
    step,
    stepsTotal,
    fulfillmentMethod,
    checkoutShippingPageLink,
    history,
    cartId,
    showPlaceOrderButton,
    showSubmitForApprovalOrder,
    xCheckoutStepperUpdate,
    xCheckoutStepsTotalUpdate,
    preloadCheckoutShippingData,
    xUpdateExpressCheckout,
    xExpressCheckout,
    updateCart,
    currentPage,
    expressCheckoutState,
    achError,
    selectedAchAccountNumber,
    achIsEditing,
    paymentMethodName,
    achPaymentMethodName,
    nextButtonDisabled,
    allowedPaymentMethodsForCheckout,
}) => {
    useEffect(() => {
        if (!expressCheckoutState.isCalled && expressCheckoutState.isEnabled) {
            xExpressCheckout({});
        }
    }, []);

    useEffect(() => {
        if (
            !expressCheckoutState.expressCheckout ||
            !expressCheckoutState.isEnabled ||
            expressCheckoutState.isLoading
        ) {
            return;
        }
        const expressCheckoutStep = stepsAsString[expressCheckoutState.expressCheckout];

        if (step === stepsAsString["Addresses"]) {
            if (expressCheckoutStep !== stepsAsString["Addresses"]) {
                document && document.getElementById("check-stepper-next")?.click();
            } else {
                xUpdateExpressCheckout({ isEnabled: false } as ExpressCheckoutCollectionModel);
            }
            return;
        }

        xUpdateExpressCheckout({ isEnabled: false } as ExpressCheckoutCollectionModel);
        xCheckoutStepperUpdate({ step: expressCheckoutStep });
    }, [expressCheckoutState.expressCheckout]);

    // <XNG-Changes> RUG-605: to move to CheckoutShipping when the step update from xCheckoutStepperUpdate
    useEffect(() => {
        if (step === 1 && currentPage.id !== checkoutShippingPageLink?.id) {
            backClickHandler();
        }
    }, [step]);
    // </XNG-Changes> RUG-605

    const applyUpdateCart = async () => {
        await updateCart({});
    };

    if (fulfillmentMethod === "PickUp" && showSubmitForApprovalOrder) {
        xCheckoutStepsTotalUpdate({ stepsTotal: 2 });
    } else if (fulfillmentMethod === "PickUp" || showSubmitForApprovalOrder) {
        xCheckoutStepsTotalUpdate({ stepsTotal: 3 });
    } else {
        xCheckoutStepsTotalUpdate({ stepsTotal: 4 });
    }

    const handleStep = (direction: string, toStep?: number) => {
        if (toStep && (step < toStep || step === toStep)) {
            return;
        }

        if (direction === "back") {
            if (toStep) {
                toStep === 1 ? backClickHandler() : xCheckoutStepperUpdate({ step: toStep });
                return;
            }
            if (step === 2 || (step === 3 && fulfillmentMethod === "PickUp")) {
                backClickHandler();
                return;
            }

            if (step === 4 && showSubmitForApprovalOrder && fulfillmentMethod === "PickUp") {
                backClickHandler();
                return;
            }

            if (step === 4 && showSubmitForApprovalOrder) {
                xCheckoutStepperUpdate({ step: 2 });
                return;
            }

            if (step > 2) {
                xCheckoutStepperUpdate({ step: step - 1 });
            }
        } else {
            if (step === 1) {
                return;
            }

            if (step === 2 && showSubmitForApprovalOrder) {
                xCheckoutStepperUpdate({ step: 4 });
                return;
            }

            if (step === 2) {
                xCheckoutStepperUpdate({ step: step + 1 });
                applyUpdateCart();
            }

            if (step === 3) {
                submitPaymentForm();
            }
        }
    };
    const submitPaymentForm = () => {
        document.getElementById("reviewAndSubmitPaymentForm-submit")?.click();
    };

    const backClickHandler = () => {
        if (checkoutShippingPageLink?.url) {
            history.push(checkoutShippingPageLink?.url);
        }

        /*
            preloadCheckoutShippingData({
                cartId,
                onSuccess: () => {
                    const backUrl = cartId
                        ? `${checkoutShippingPageLink!.url}?cartId=${cartId}`
                        : checkoutShippingPageLink!.url;
                    history.push(checkoutShippingPageLink!.url);
                },
            });
        */
    };

    const getClassName = (currentStep: number) => {
        let classes = "stepNode";
        if (currentStep === step) {
            classes = `stepNode active`;
        }
        if (step > currentStep) {
            classes = `stepNode done`;
        }
        return classes;
    };

    const getStepNumber = () => {
        if (fulfillmentMethod === "PickUp" && showSubmitForApprovalOrder) {
            return "2. ";
        }
        if (fulfillmentMethod === "PickUp" || showSubmitForApprovalOrder) {
            return "3. ";
        }
        return "4. ";
    };

    const progressIndicatorStep = () => {
        if (
            (fulfillmentMethod === "PickUp" && !showSubmitForApprovalOrder) ||
            (showSubmitForApprovalOrder && step === 4)
        ) {
            // we need "or" to prevent returns zero
            return step - 1 || 1;
        }
        return step || 1;
    };

    const stepsHtml = () => {
        return (
            <>
                <Link
                    className={getClassName(1)}
                    {...styles.step}
                    onClick={() => {
                        handleStep("back", 1);
                    }}
                >
                    1. {translate("Addresses")}
                </Link>

                {fulfillmentMethod === "Ship" && (
                    <Link
                        className={getClassName(2)}
                        {...styles.step}
                        onClick={() => {
                            handleStep("back", 2);
                        }}
                    >
                        2. {translate("Shipping")}
                    </Link>
                )}
                {!showSubmitForApprovalOrder && (
                    <Link
                        className={getClassName(3)}
                        {...styles.step}
                        onClick={() => {
                            handleStep("back", 3);
                        }}
                    >
                        {fulfillmentMethod === "PickUp" ? "2. " : "3. "}
                        {translate("Payment")}
                    </Link>
                )}
                <Link as="div" className={getClassName(4)} {...styles.step}>
                    {getStepNumber()}
                    {translate("Review")}
                </Link>
            </>
        );
    };

    /**
     * XNG-Change
     * RUG-1568: disable next button if allowedPaymentMethodsForCheckout are empty
     * Back-end returns it as [""] if no allowedPaymentMethodsForCheckout are authored
     * using condition:
     * || (allowedPaymentMethodsForCheckout.length === 1 && allowedPaymentMethodsForCheckout[0] === "")
     */

    const isNextButtonDisabled =
        nextButtonDisabled ||
        (step === 3 &&
            ((paymentMethodName === achPaymentMethodName &&
                (!selectedAchAccountNumber || !!achError || achIsEditing)) ||
                (allowedPaymentMethodsForCheckout.length === 1 && allowedPaymentMethodsForCheckout[0] === "")));

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.stepsContainer} {...({ stepsTotal } as any)}>
                <StyledWrapper
                    {...styles.progressIndicator}
                    {...({ step: progressIndicatorStep(), stepsTotal } as any)}
                />

                {stepsHtml()}
            </StyledWrapper>

            <StyledWrapper {...styles.buttonWrapper}>
                <Button
                    {...styles.button}
                    className={`prev ${step === 4 ? "full-width" : ""} ${step === 1 ? "hide" : ""}`}
                    variant="secondary"
                    onClick={() => {
                        handleStep("back");
                    }}
                >
                    {translate("Prev")}
                </Button>
                <Button
                    id="check-stepper-next"
                    type={step === 1 ? "submit" : "button"}
                    className={`next ${step === 1 ? "full-width" : ""}  ${step === 4 ? "hide" : ""}`}
                    disabled={isNextButtonDisabled}
                    {...styles.button}
                    onClick={() => {
                        handleStep("forward");
                    }}
                >
                    {translate("Next")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutStepper)),
    definition: {
        displayName: "Checkout Stepper",
        group: "Xngage Custom" as any,
        allowedContexts: [CheckoutShippingPageContext, CheckoutReviewAndSubmitPageContext],
    },
};

export default widgetModule;
